import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Reusable dropdown action menu component.
 * Uses portal to render dropdown outside parent stacking context.
 */
export default function ActionMenu({ actions = [], label = 'Akcje', size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Calculate position when opening
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 200;
      const dropdownWidth = 160;

      let top = rect.bottom + 4;
      let left = rect.right - dropdownWidth;

      // If dropdown would go below viewport, open upward
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 4;
      }

      // If dropdown would go off left edge
      if (left < 8) {
        left = 8;
      }

      setPosition({ top, left });
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [open]);

  const visibleActions = actions.filter(Boolean);
  if (visibleActions.length === 0) return null;

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(prev => !prev);
  };

  const dropdown = open ? createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 999999,
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        border: '1px solid var(--border)',
        minWidth: '160px',
        overflow: 'hidden'
      }}
    >
      {visibleActions.map((action, i) => (
        <button
          type="button"
          key={i}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
            action.onClick?.();
          }}
          disabled={action.disabled}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            background: 'var(--card-bg)',
            border: 'none',
            color: action.disabled ? 'var(--text-muted)' : (action.color || 'var(--text)'),
            textAlign: 'left',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderBottom: i < visibleActions.length - 1 ? '1px solid var(--border)' : 'none',
            opacity: action.disabled ? 0.5 : 1,
            cursor: action.disabled ? 'not-allowed' : 'pointer'
          }}
        >
          <span style={{ fontSize: '12px', width: '16px', textAlign: 'center' }}>
            {action.icon}
          </span>
          {action.label}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={handleToggle}
        style={{
          padding: '4px 10px',
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          fontWeight: open ? 700 : 400,
          cursor: 'pointer',
          border: '1px solid var(--border)',
          backgroundColor: open ? 'var(--primary)' : 'var(--card-bg)',
          color: open ? '#fff' : 'var(--text)'
        }}
      >
        {'⚙️'} {label} {open ? '▴' : '▾'}
      </button>
      {dropdown}
    </>
  );
}
